import type { JSX } from 'react'
import type { SearchResults } from '../../api/search.js'

const SECTION_ICONS: Record<string, string> = {
  Conversations: 'pi pi-comments',
  Contacts: 'pi pi-user',
  Calls: 'pi pi-phone',
  Recordings: 'pi pi-video',
  Agents: 'pi pi-users',
  Messages: 'pi pi-send',
}

export interface FlatResult {
  key: string
  href: string
  primary: string
  secondary?: string
  section: string
  /**
   * Phase-2 Batch C1 — ts_headline match snippet. May contain
   * `<b>...</b>` highlight tags. Sanitised via {@link sanitiseSnippet}
   * before injection (whitelist `<b>` / `</b>` only). Recordings have no
   * text source and never carry a snippet.
   */
  snippet?: string
}

/**
 * Strip every HTML tag from a ts_headline snippet EXCEPT `<b>` and
 * `</b>`. The server is the only writer (no user content reaches this
 * column), but we still whitelist defensively before
 * dangerouslySetInnerHTML so a future server bug can never inject
 * `<script>` / `<img onerror=...>`. Also escapes `<` / `>` characters
 * that the user typed into the original text so they cannot construct
 * fake tags via the source column.
 */
export function sanitiseSnippet(raw: string): string {
  // 1. Escape literal `<` and `>` from user content to `&lt;` / `&gt;`.
  //    ts_headline wraps matches with `<b>...</b>` so we restore those
  //    after escaping.
  const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // 2. Re-emit the ts_headline highlights. The simple regex is
  //    sufficient because ts_headline only ever emits `<b>` / `</b>`
  //    with the default StartSel/StopSel; we never opt into custom
  //    delimiters.
  return escaped.replace(/&lt;b&gt;/g, '<b>').replace(/&lt;\/b&gt;/g, '</b>')
}

/**
 * Flattens SearchResults into the activation order used by Arrow Up/Down +
 * Enter: conversations → contacts → calls → recordings → agents.
 *
 * `key` is unique-per-result; `section` mirrors the section heading.
 */
export function flattenResults(results: SearchResults | null): FlatResult[] {
  if (!results) return []
  const flat: FlatResult[] = []

  for (const c of results.conversations) {
    flat.push({
      key: `conversation:${c.id}`,
      href: `/conversations/${c.id}`,
      primary: c.subject?.trim() ? c.subject : c.referenceId,
      secondary: `${c.referenceId} · ${c.channelOrigin}`,
      section: 'Conversations',
      ...(c.snippet !== undefined ? { snippet: c.snippet } : {}),
    })
  }
  for (const ct of results.contacts) {
    flat.push({
      key: `contact:${ct.id}`,
      href: `/admin/contacts`,
      primary: ct.name,
      secondary: ct.email ? `${ct.phoneE164} · ${ct.email}` : ct.phoneE164,
      section: 'Contacts',
      ...(ct.snippet !== undefined ? { snippet: ct.snippet } : {}),
    })
  }
  for (const call of results.calls) {
    flat.push({
      key: `call:${call.id}`,
      href: `/admin/recordings?callId=${call.id}`,
      primary: `${call.fromNumber} → ${call.toNumber}`,
      secondary: `${call.uniqueId} · ${call.startTime}`,
      section: 'Calls',
      ...(call.snippet !== undefined ? { snippet: call.snippet } : {}),
    })
  }
  for (const r of results.recordings) {
    flat.push({
      key: `recording:${r.id}`,
      href: `/admin/recordings?callId=${r.callId}`,
      primary: `Recording (${r.durationSec}s)`,
      secondary: r.createdAt,
      section: 'Recordings',
      // Recordings has no direct text source → no snippet, ever.
    })
  }
  for (const a of results.agents) {
    flat.push({
      key: `agent:${a.id}`,
      href: `/admin/user-management/users`,
      primary: a.userFullName ?? `Extension ${a.extension}`,
      secondary: `ext ${a.extension}`,
      section: 'Agents',
      ...(a.snippet !== undefined ? { snippet: a.snippet } : {}),
    })
  }

  return flat
}

/**
 * Phase-2 Batch C2 — result-type filter chips.
 *
 * The SearchPalette chip row sets this prop. Default 'all' renders every
 * non-empty bucket; any other value scopes the visible sections to the
 * matching bucket only. Recordings is intentionally a first-class chip
 * even though it has no snippet — the bucket still surfaces a click
 * target back to the recording.
 */
export type ResultScope =
  | 'all'
  | 'conversations'
  | 'contacts'
  | 'calls'
  | 'recordings'
  | 'agents'
  | 'messages'

interface ResultSectionsProps {
  results: SearchResults | null
  activeIndex: number
  onSelect: (href: string) => void
  /**
   * When set to anything other than 'all', renders only the matching
   * bucket. Default 'all'.
   */
  scope?: ResultScope
}

/**
 * Mask a SearchResults so that only the scoped bucket has rows. Used by
 * the chip filter so a chip click can hide all other buckets without
 * mutating the upstream cache or re-fetching from the backend.
 *
 * Exported so the SearchPalette can apply the same mask to its
 * keyboard-navigation flat list — Enter must always activate a visible
 * row, never a hidden one.
 */
export function applyScope(
  results: SearchResults | null,
  scope: ResultScope,
): SearchResults | null {
  if (!results) return null
  if (scope === 'all') return results
  const empty: SearchResults = {
    conversations: [],
    contacts: [],
    calls: [],
    recordings: [],
    agents: [],
    messages: [],
  }
  return { ...empty, [scope]: results[scope] }
}

/**
 * Renders the five result sections (conversations, contacts, calls, recordings,
 * agents) preserving the flat activation order used by keyboard navigation.
 * The row at `activeIndex` is highlighted and announced via aria-selected.
 *
 * Phase-2 Batch C2 — when `scope !== 'all'`, only the matching bucket is
 * rendered; the others are masked to empty before flattening so keyboard
 * navigation stays correct (activeIndex always indexes into the visible
 * rows).
 */
export function ResultSections({
  results,
  activeIndex,
  onSelect,
  scope = 'all',
}: ResultSectionsProps): JSX.Element {
  const scopedResults = applyScope(results, scope)
  const flat = flattenResults(scopedResults)

  // Group rows by section while preserving flat-index for highlight + keys.
  const grouped: { section: string; rows: { row: FlatResult; flatIndex: number }[] }[] = []
  flat.forEach((row, i) => {
    const last = grouped[grouped.length - 1]
    if (!last || last.section !== row.section) {
      grouped.push({ section: row.section, rows: [{ row, flatIndex: i }] })
    } else {
      last.rows.push({ row, flatIndex: i })
    }
  })

  return (
    <ul role="listbox" aria-label="Search results">
      {grouped.map((g) => (
        <li key={g.section}>
          <div className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 select-none">
            <i className={`${SECTION_ICONS[g.section] ?? 'pi pi-circle'} text-[10px]`} aria-hidden />
            <span>{g.section}</span>
          </div>
          <ul>
            {g.rows.map(({ row, flatIndex }) => {
              const isActive = flatIndex === activeIndex
              return (
                <li
                  key={row.key}
                  data-testid={`search-result-${row.key}`}
                  data-flat-index={flatIndex}
                  role="option"
                  aria-selected={isActive}
                  className={[
                    'group cursor-pointer flex items-start gap-3 border-l-2 px-4 py-2.5 transition-colors',
                    isActive
                      ? 'border-slate-800 bg-slate-50/80'
                      : 'border-transparent hover:bg-slate-50/60',
                  ].join(' ')}
                  onMouseDown={(e) => {
                    // mousedown — fires before the input loses focus / triggers blur close
                    e.preventDefault()
                    onSelect(row.href)
                  }}
                >
                  <div className="mt-0.5">
                    <i
                      className={`${SECTION_ICONS[g.section] ?? 'pi pi-circle'} text-xs text-slate-400 group-hover:text-slate-500 ${isActive ? 'text-slate-600' : ''}`}
                      aria-hidden
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800">
                      {row.primary}
                    </div>
                    {row.secondary && (
                      <div className="truncate text-xs text-slate-500">
                        {row.secondary}
                      </div>
                    )}
                    {row.snippet && (
                      <div
                        data-testid={`search-result-snippet-${row.key}`}
                        className="mt-1 text-xs leading-relaxed text-slate-500 [&_b]:font-semibold [&_b]:text-slate-700"
                        // Server-generated snippet from ts_headline. The
                        // payload only ever contains `<b>...</b>` highlight
                        // tags after sanitiseSnippet whitelisting; user
                        // content is escaped first so injecting HTML is
                        // safe. See sanitiseSnippet for the precise rules.
                        dangerouslySetInnerHTML={{
                          __html: sanitiseSnippet(row.snippet),
                        }}
                      />
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </li>
      ))}
    </ul>
  )
}

export default ResultSections
